import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { EditSolidIcon, PlusIcon } from '@dhl-ui/icons'
import { type GridColDef, Pagination } from '@dhl/components'
import { Box, Button, Grid, IconButton, Paper } from '@dhl-ui/components'
import { useDataAsyncCall } from '~/api/admin-api'

import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField, useSearchAndPage } from '~/common'
import type { ServicepointVendor } from '~/modules/servicepointvendors/model'

type Response = {
  vendors: ServicepointVendor[]
}

export const ServicePointVendorsPage = () => {
  const { t } = useTranslation()
  const [vendors, setVendors] = useState<ServicepointVendor[]>()
  const [initVendors, setInitVendors] = useState<ServicepointVendor[]>([])
  const navigate = useNavigate()
  const { page, setPage, pageSize, setPageSize } = useSearchAndPage()
  const [response, meta] = useDataAsyncCall<Response>(`vendors`)

  const handleSearchChange = useCallback(
    (search: string) => {
      setPage(0)
      setVendors(
        search
          ? initVendors.filter(
              ({ address: { city }, name, externalId }) =>
                city.toLowerCase().indexOf(search) !== -1 ||
                name.toLowerCase().indexOf(search) !== -1 ||
                externalId.toLowerCase().indexOf(search) !== -1
            )
          : initVendors
      )
    },
    [initVendors]
  )

  useEffect(() => {
    setVendors(response?.vendors || [])
    setInitVendors(response?.vendors || [])
  }, [response])

  const getCityValue = (row: ServicepointVendor) => row?.address?.city

  const onClickEdit = (row: ServicepointVendor) => {
    navigate(links.editServicePointVendorLink(row?.id || ''))
  }

  const handleRowClick = (row: ServicepointVendor) => {
    navigate(links.showServicePointVendorDetailsLink(row?.id || ''))
  }

  const renderCellEdit = (row: ServicepointVendor) => {
    return (
      <IconButton aria-label="edit servicepoints" onClick={() => onClickEdit(row)}>
        <EditSolidIcon data-testid="edit-servicepoints" />
      </IconButton>
    )
  }

  const columns: GridColDef<ServicepointVendor>[] = [
    {
      field: 'name',
      headerName: t('admin.servicePointVendorsPage.userHeader.name'),
      flex: 1,
      sortable: true,
    },
    {
      field: 'externalId',
      headerName: t('admin.servicePointVendorsPage.userHeader.servicePointId'),
      flex: 1,
      sortable: true,
    },
    {
      field: 'city',
      headerName: t('admin.servicePointVendorsPage.userHeader.city'),
      flex: 1,
      valueGetter: (value, row) => getCityValue(row),
      sortable: true,
    },
    {
      field: 'actions',
      headerName: t('admin.servicePointVendorsPage.userHeader.actions'),
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => renderCellEdit(row),
      disableReorder: true,
      disableExport: true,
      resizable: false,
      filterable: false,
      editable: false,
    },
  ]

  return (
    <>
      <Box mb={3}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          data-testid="servicepoint-vendors-page"
        >
          <Grid>
            <SearchTextField onChange={handleSearchChange} transform={lowercaseTransform} />
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              data-testid="add-servicepoint"
              onClick={() => navigate(links.addServicePointVendorLink())}
              startIcon={<PlusIcon />}
            >
              {t('admin.servicePointVendorsPage.userHeader.addServicepointVendorButtonTitle')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={meta.isLoading}
            data-testid="servicepoints-table"
            autoHeight
            rows={vendors ?? []}
            columns={columns}
            paginationModel={{
              page,
              pageSize,
            }}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={vendors?.length ?? 0}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      </Box>
    </>
  )
}
